import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { ModalRef, ModalData } from '@purespectrum1/ui/modal';

import { Subscription } from 'rxjs';

import { BuyerApiService } from '../../shared/services/buyer-api/survey.service';
import { SupplierInterface } from '../../manage-survey-tabs/interfaces/manage-suppliers.interface';
import { PricingDetails } from '../../shared/interfaces/cpi.interface';
import { LayoutConstants } from '../../create-surveys/layout/layout-constants';
import { SupplierApiService } from '../../shared/services/supplier-api/supplier-api.service';
import { FlexibilityPricesParams } from '../../shared/interfaces/supplier-api.interface';
import { ToasterService } from '@purespectrum1/ui/toaster-service';

@Component({
  selector: 'ps-ui-launch-modal',
  templateUrl: './launch-modal.component.html',
  styleUrls: ['./launch-modal.component.css'],
})
export class LaunchModalComponent implements OnInit, OnDestroy {
  private _subscriptions: Subscription = new Subscription();
  private _surveyId!: number;
  public pricingDetails: PricingDetails = {} as PricingDetails;
  public isPriceOverride: boolean = false;
  public pricingAccepted: boolean = false;
  public isPriceGreaterThanCpiThreshold: boolean = false;
  public maxCpiThershold: number = LayoutConstants.MAX_CPI_THRESHOLD;

  constructor(
    private _modalRef: ModalRef<LaunchModalComponent, string>,
    @Inject(ModalData)
    private _modalData: {
      surveyId: number;
    },
    private _buyerApiService: BuyerApiService,
    private _supplierApiService: SupplierApiService,
    private _toastr: ToasterService
  ) {}

  ngOnInit(): void {
    this._surveyId = this._modalData.surveyId;
    this._supplierApiService
      .getFlexibilityPricesParams(this._surveyId)
      .subscribe((price: FlexibilityPricesParams) => {
        this._getPricingDetails(price);
      });
  }

  close(message: string) {
    if (message === 'Launch') {
      const confirmationMsg = `Your survey is set to ${this.pricingDetails.type} with CPI ${this.pricingDetails.value}`;
      if (!this.pricingAccepted && this.isPriceGreaterThanCpiThreshold) {
        return this._toastr.error('Please accept pricing to proceed further.');
      }
      const addPricingConfirmationLog$ = this._buyerApiService
        .addPricingConfirmationLog(this._surveyId, confirmationMsg)
        .subscribe((_res) => {
          this._modalRef.close(message);
        });
      this._subscriptions.add(addPricingConfirmationLog$);
    } else {
      this._modalRef.close();
    }
  }

  private _getPricingDetails(price: FlexibilityPricesParams) {
    if (price.survey_price_override) {
      this.pricingDetails.type = LayoutConstants.PRICING_TYPE.PRICE_OVERRIDE;
      this.pricingDetails.value = `${price.currency}${price.survey_price_override}`;
      this.isPriceOverride = true;
      this.isPriceGreaterThanCpiThreshold =
        price.survey_price_override > this.maxCpiThershold ? true : false;
    } else if (price.manual_override) {
      this.pricingDetails.type =
        LayoutConstants.PRICING_TYPE.SELECTIVE_MANUAL_OVERRIDE;
      this.isPriceOverride = true;
      this._getSuppliers(this._surveyId);
    } else {
      this.pricingDetails.type = LayoutConstants.PRICING_TYPE.SUPPLIER_RATES;
      this.isPriceOverride = false;
      this._getSuppliers(this._surveyId);
    }
  }

  private _getSuppliers(surveyId: number) {
    const getSuppliers$ = this._buyerApiService
      .getSuppliers(surveyId, 'editable')
      .subscribe((suppliers: SupplierInterface[]) => {
        const activeSuppliers = suppliers.filter(
          (supplier) => supplier.is_active === true && supplier.quantity > 0
        );
        const currencySymbol = suppliers[0].currency_symbol;
        const lowestSupplierCPI = Math.min.apply(
          Math,
          activeSuppliers.map((supplier) => supplier.cpi)
        );
        const highestSupplierCPI = Math.max.apply(
          Math,
          activeSuppliers.map((supplier) => supplier.cpi)
        );
        this.pricingDetails.value = `Ranging from: ${currencySymbol}${lowestSupplierCPI} - ${currencySymbol}${highestSupplierCPI}`;
        this.isPriceGreaterThanCpiThreshold =
          highestSupplierCPI > this.maxCpiThershold ? true : false;
      });
    this._subscriptions.add(getSuppliers$);
  }

  ngOnDestroy() {
    this._subscriptions.unsubscribe();
  }
}
